/**
 * useDemoMode
 *
 * Fetches `/api/financials/demo/status` and exposes seed / clear helpers.
 * When Gary wants to give a walkthrough on demo data, one click seeds the
 * static demo dataset for his tenant. A real QuickBooks connect purges
 * the seed automatically on the backend, so real data always wins.
 */
import { useCallback, useEffect, useState } from "react";
import api from "../utils/api";

export function useDemoMode() {
  const [status, setStatus] = useState({ active: false, quickbooks_live: false, source: "none" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/financials/demo/status");
      setStatus(res.data || { active: false });
    } catch {
      setStatus({ active: false, quickbooks_live: false, source: "none" });
    } finally {
      setLoading(false);
    }
  }, []);

  const seed = useCallback(async () => {
    setBusy(true);
    try {
      await api.post("/financials/demo/seed");
      await refresh();
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err?.response?.data?.detail || "seed_failed" };
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  const clear = useCallback(async () => {
    setBusy(true);
    try {
      await api.delete("/financials/demo");
      await refresh();
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err?.response?.data?.detail || "clear_failed" };
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  useEffect(() => { refresh(); }, [refresh]);

  return { status, loading, busy, seed, clear, refresh };
}
